import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppState, useSetAppState } from "../../../stores/appContext";
import { useAnswerersV2 } from "../../answerer/hooks/useAnswerers";
import {
    createAnswererInputSchema,
    type CreateAnswererInput,
} from "../../answerer/schemas/answererSchemas";
import type { Answerer } from "../../../api/generated";
export {
    createAnswererInputSchema,
    updateAnswererInputSchema,
    receiverNicknameSchema,
    type CreateAnswererInput,
    type UpdateAnswererInput,
    type ReceiverNicknameInput,
} from "../../answerer/schemas/answererSchemas";
export { useCreateNewAnswerer } from "./useCreateNewAnswerer";
export { useSendVerifyEmailToNewAnswerer } from "./useSendVerifyEmailToNewAnswerer";

/**
 * 新規回答者作成フロー全体を管理する統合Hook
 *
 * 自動保存の仕組み：
 *   - form.watch()で全フィールドを監視
 *   - JSON.stringifyで文字列化してuseMemoで安定した参照を作る
 *   - これにより実際に値が変わったときだけ保存される
 *
 * 入力ページ（ReceiverNicknamePage、AnswererRequiredInfoPage）:
 *   - formで入力を管理
 *   - formの値は自動的にAppContextに保存される
 *
 */
export const useNewAnswererInfo = () => {
    const state = useAppState();
    const setAppState = useSetAppState();
    const { newAnswererInfo } = state;
    const { answerers = [] } = useAnswerersV2({ isInit: true });

    // answererが変更されたときにresolverを再作成
    const resolver = useMemo(
        () => zodResolver(createAnswererInputSchema(answerers)),
        [answerers]
    );

    const form = useForm<CreateAnswererInput>({
        mode: "onChange",
        resolver,
        defaultValues: {
            name: newAnswererInfo.name,
            relationship: newAnswererInfo.relationship,
            birthday: newAnswererInfo.birthday,
            receiverNickname: newAnswererInfo.receiverNickname,
            email: newAnswererInfo.email,
            questionSendType: newAnswererInfo.questionSendType,
            qmailFrequency: newAnswererInfo.qmailSendFrequency,
        },
    });

    // 初期表示時にバリデーションを実行（一度だけ）
    useEffect(() => {
        form.trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // formの値を監視して、変更があれば自動的にAppContextに保存
    const formValues = form.watch();

    // formValuesを文字列化して、変更があればAppContextに保存
    const formValuesKey = useMemo(
        () => JSON.stringify(formValues),
        [formValues]
    );

    useEffect(() => {
        setAppState((prev) => ({
            ...prev,
            newAnswererInfo: {
                ...prev.newAnswererInfo,
                name: formValues.name,
                relationship: formValues.relationship,
                birthday: formValues.birthday,
                receiverNickname: formValues.receiverNickname,
                email: formValues.email,
                questionSendType: formValues.questionSendType,
                qmailSendFrequency: formValues.qmailFrequency,
            },
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValuesKey]);

    // answerers リストに追加（回答者作成後）
    // 同時にanswererId も設定する
    const addAnswerer = (answerer: Answerer) => {
        setAppState((prev) => ({
            ...prev,
            newAnswererInfo: {
                ...prev.newAnswererInfo,
                answererId: answerer.answererId,
                answerers: [...prev.newAnswererInfo.answerers, answerer],
            },
        }));
    };

    return {
        form,
        answererId: newAnswererInfo.answererId,
        newAnswererInfo,
        addAnswerer,
    };
};
